import { useEffect, useState } from 'react';
import { Box, MenuItem, Paper, TextField } from '@mui/material';
import { NumericTextField } from '../../../../components/forms/NumericTextField';
import type { RockUCSGeneral } from '../../../../types/rockUcs';
import { fetchMachines, type MachineRow } from '../../../../services/scheduleMachines';
import { CHAIN_LENGTH_DEFAULT_MM } from '../rockUcsFields';
import { DerivedValue, fieldGridSx, fmt, inputSx, paperSx, SectionHeader, unit } from '../sectionStyles';

interface Props {
  general: RockUCSGeneral;
  avgGaugeLengthMm: number | null;
  onChange: (field: keyof RockUCSGeneral, value: number | string | null) => void;
  readOnly?: boolean;
}

/**
 * REQ-04 — Compression Test Setup (entered after the test): 3 gauge lengths →
 * average (drives strain calcs), chain length (reference only, default by core
 * size), rate of axial strain (default 0.06 %/min), machine reference, test
 * date, tested-by initials.
 *
 * DIFFERENCE from Rock TCS: the "Confining Stress σ₃" NumericTextField is
 * intentionally absent here. UCS is uniaxial (σ₃ = 0 by definition, item 1.5).
 */
export const CompressionSetupSection = ({ general, avgGaugeLengthMm, onChange, readOnly }: Props) => {
  const [machines, setMachines] = useState<MachineRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetchMachines()
      .then((rows) => {
        if (cancelled) return;
        // Rock triaxial/UCS machines first (RK-TX…), then the rest of the rock fleet
        const sorted = [...rows].sort((a, b) => {
          const aTx = a.Code?.startsWith('RK-TX') ? 0 : 1;
          const bTx = b.Code?.startsWith('RK-TX') ? 0 : 1;
          return aTx - bTx || (a.Code ?? '').localeCompare(b.Code ?? '');
        });
        setMachines(sorted);
      })
      .catch(() => setMachines([]));
    return () => { cancelled = true; };
  }, []);

  // Default the chain length when the core size has a known default and the
  // field is still empty (kept for reference — not used in calculations).
  useEffect(() => {
    const def = general.coreSize ? CHAIN_LENGTH_DEFAULT_MM[general.coreSize] : null;
    if (general.chainLengthMm == null && def != null) {
      onChange('chainLengthMm', def);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [general.coreSize]);

  return (
    <Paper variant="outlined" sx={paperSx}>
      <SectionHeader label="4. Compression Test Setup" />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: 1.5 }}>
        <Box sx={fieldGridSx}>
          {[1, 2, 3].map((i) => (
            <NumericTextField
              key={`gl${i}`}
              size="small" label={`Gauge Length ${i}`}
              value={general[`gaugeLength${i}` as keyof RockUCSGeneral] as number | null}
              onChange={(v) => onChange(`gaugeLength${i}` as keyof RockUCSGeneral, v)}
              InputProps={{ endAdornment: unit('mm'), readOnly }} sx={inputSx}
            />
          ))}
          <NumericTextField
            size="small" label="Chain Length (reference)"
            value={general.chainLengthMm}
            onChange={(v) => onChange('chainLengthMm', v)}
            InputProps={{ endAdornment: unit('mm'), readOnly }} sx={inputSx}
          />
          {/* Confining Stress σ₃ intentionally omitted — UCS is uniaxial (item 1.5 DROP decision) */}
          <NumericTextField
            size="small" label="Rate of Axial Strain"
            value={general.rateOfAxialStrainPctMin}
            onChange={(v) => onChange('rateOfAxialStrainPctMin', v)}
            InputProps={{ endAdornment: unit('%/min'), readOnly }} sx={inputSx}
          />
        </Box>

        <Box sx={{ display: 'grid', rowGap: 1.25, alignContent: 'start' }}>
          <TextField
            select size="small" label="Machine"
            value={general.machineKey ?? ''}
            onChange={(e) => onChange('machineKey', e.target.value || null)}
            InputProps={{ readOnly }} sx={inputSx}
          >
            <MenuItem value="">—</MenuItem>
            {machines.map((m) => (
              <MenuItem key={m.MachineKey} value={m.MachineKey}>
                {m.Code} — {m.Name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small" label="Test Date" type="date"
            value={general.testDate ? String(general.testDate).slice(0, 10) : ''}
            onChange={(e) => onChange('testDate', e.target.value || null)}
            InputLabelProps={{ shrink: true }}
            InputProps={{ readOnly }} sx={inputSx}
          />
          <TextField
            size="small" label="Tested By (initials)"
            value={general.testedBy ?? ''}
            onChange={(e) => onChange('testedBy', e.target.value || null)}
            InputProps={{ readOnly }} sx={inputSx}
          />
        </Box>

        <Box sx={{ display: 'grid', rowGap: 0.5, alignContent: 'start', px: 1 }}>
          <SectionHeader label="Derived" color="text.secondary" />
          <DerivedValue label="Avg Gauge Length" value={fmt(avgGaugeLengthMm, 2)} suffix="mm" />
        </Box>
      </Box>
    </Paper>
  );
};
